import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useCallback, useEffect, useMemo } from "react";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { ItemType } from "@/domains/inventory/constants/itemType";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  equipmentSelector,
  inventoryByIdSelector,
  setMemoizedItem,
} from "@/domains/inventory/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import { determineEquipmentSlotType } from "@/domains/inventory/helpers/determineEquipmentSlotType";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import isEquipmentSlot from "@/domains/inventory/helpers/isEquipmentSlot";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useGetEquipment from "@/domains/inventory/hooks/useGetEquipment";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";
import useGetQuickSlots from "@/domains/inventory/hooks/useGetQuickSlots";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryCharacterSlotsContainer from "@/domains/inventory/containers/character/InventoryCharacterSlotsContainer";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryCoolDown from "@/domains/inventory/components/InventoryCoolDown";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventorySeparated from "@/domains/inventory/components/InventorySeparated";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryCharacterIcon from "@/domains/inventory/components/character/InventoryCharacterIcon";
import InventoryCharacterHeader from "@/domains/inventory/components/character/InventoryCharaterHeader";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";

type Props = { inventoryId: number };

export default function InventoryCharacterContainer({ inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();

  const dispatch = useAppDispatch();
  const playerInfo = useAppSelector(playerInfoSelector);
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );

  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);
  const equipment = useAppSelector(equipmentSelector);
  const { getInventory } = useGetInventory();
  const { getEquipment } = useGetEquipment();
  const { getQuickSlots } = useGetQuickSlots();
  const {
    moveItem,
    equipItem,
    unEquipItem,
    unassignQuickSlotItem,
    stackItems,
    attachItem,
    applyItem,
    detachItem,
    dropItem,
    splitItem,
    pickItem,
  } = useInventoryManager();

  useEffect(() => {
    getEquipment({
      characterId: Number(inventory.ownerId),
    });
    getQuickSlots({
      characterId: Number(inventory.ownerId),
    });
  }, [inventory.ownerId]);

  const onRefresh = useCallback(async () => {
    if (inventory) {
      getEquipment({
        characterId: Number(inventory.ownerId),
      });
      getQuickSlots({
        characterId: Number(inventory.ownerId),
      });
    }
  }, [inventory]);

  const momoizedItemHandler = useCallback(
    (item: InventoryItem | null) => dispatch(setMemoizedItem(item)),
    [],
  );

  const doubleClickItemHandler = useCallback(
    (item: InventoryItem) => {
      switch (item.itemTemplate.type) {
        case OwnerType.Bag: {
          getInventory({
            inventoryOwnerId: item.id,
            inventoryOwnerType: InventoryOwnerType.Bag,
          });
          return;
        }
        default:
          const slot = determineEquipmentSlotType(item.itemTemplate.type as ItemType);
          if (isEquipmentSlot(slot) && equipment?.id) {
            equipItem(equipment.id, {
              slot,
              item,
            });
          }
      }
    },
    [equipment],
  );

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  const onCloseHandler = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, []);

  if (!playerInfo || !equipment) return null;

  return (
    <InventoryWrapper className="max-h-full">
      <InventoryHeader className="px-0">
        <InventoryCharacterHeader player={playerInfo} onClose={onCloseHandler} />
      </InventoryHeader>

      <InventoryContent className="flex flex-col gap-5.5 relative py-7.5">
        <InventoryCharacterIcon />
        <InventoryCoolDown
          percent={playerInfo.player.level}
          size="md"
          className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
        />
        <InventoryCharacterSlotsContainer onRefresh={onRefresh} />
      </InventoryContent>
      <InventoryContent className="flex items-center justify-between h-[3.75rem] border-t border-[#191919]">
        <div className="text-18 font-medium">Инвентарь</div>
        <div className="flex items-center gap-5">
          <InventorySearch value={deferredSearchValue} onChange={onChangeSearchValue} />
          {filterGroups.length > 0 && (
            <>
              <InventorySeparated />
              <InventoryFilter
                groups={filterGroups}
                selectedGroups={selectedGroups}
                onSelect={onSelectHandler}
              />
            </>
          )}
        </div>
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto pb-5">
        <InventoryGrid
          inventoryId={inventory.id}
          equipmentId={equipment?.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          disabledItems={disabledItems}
          items={inventory.items}
          onRefresh={onRefresh}
          onMove={moveItem}
          onUnEquip={unEquipItem}
          onUnassignQuickSlot={unassignQuickSlotItem}
          onStack={stackItems}
          onAttach={attachItem}
          onMemoizedItem={momoizedItemHandler}
          onDoubleClickItem={doubleClickItemHandler}
          onPick={pickItem}
          onContextMenu={handleContextMenu}
        />
        {itemForContextMenu && isOpen && (
          <InventoryContextMenu
            ref={menuRef}
            equipmentId={equipment.id}
            position={position}
            item={itemForContextMenu}
            onApply={applyItem}
            onDetach={detachItem}
            onDrop={dropItem}
            onEquip={equipItem}
            onSplit={splitItem}
            onClose={closeContextMenu}
            onRefresh={onRefresh}
          />
        )}
      </InventoryContent>
      <InventoryFooter>
        <InventoryWeight
          weight={inventory.weight + (equipment.weight || 0)}
          capacityWeight={inventory.maxWeight}
        />
      </InventoryFooter>
    </InventoryWrapper>
  );
}
